import React, { useState, useRef } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

/**
 * Componente CustomAvatarSelect para seleção de avatares com drag & drop
 * Props:
 * - options: array de objetos { value, label }
 * - value: valor selecionado (index do avatar)
 * - onChange: função chamada ao mudar (recebe string)
 * - placeholder: texto placeholder
 */
export const CustomAvatarSelect = ({
    options = [],
    value = '',
    onChange,
    placeholder = 'Selecione um avatar',
}) => {
    const [dragOverType, setDragOverType] = useState(null); // null | 'avatar' | 'other'
    const selectRef = useRef(null);

    // Handlers internos de drag and drop
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const isAvatar = !!e.dataTransfer.types.find(t => t === 'application/avatar');
        setDragOverType(isAvatar ? 'avatar' : 'other');
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverType(null);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const data = e.dataTransfer.getData('application/avatar');
        
        if (!data) {
            setDragOverType(null);
            return;
        }
        
        try {
            const avatar = JSON.parse(data);
            const newValue = avatar.index.toString();
            
            // Força atualização do select
            if (selectRef.current) {
                selectRef.current.value = newValue;
            }
            
            // Chama onChange
            if (onChange) {
                onChange(newValue);
            }
            
        } catch (error) {
            console.error('CustomAvatarSelect: error parsing avatar data:', error);
        }
        
        setDragOverType(null);
    };

    // Handler de mudança do select
    const handleChange = (e) => {
        onChange && onChange(e.target.value);
    };

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <select
                ref={selectRef}
                value={value}
                onChange={handleChange}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{ 
                    width: '100%', 
                    padding: '12px', 
                    fontSize: '14px',
                    border: dragOverType === 'avatar' ? '2px dashed #4CAF50' : dragOverType === 'other' ? '2px dashed #f44336' : '2px dashed #ddd', 
                    borderRadius: '8px',
                    background: dragOverType === 'avatar' ? '#e8f5e8' : dragOverType === 'other' ? '#ffeaea' : '#fafafa',
                    color: dragOverType === 'avatar' ? '#2e7d32' : '#333',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    outline: 'none'
                }}
            >
                <option value="" disabled>
                    {dragOverType === 'avatar' ? '🎯 Solte o avatar aqui!' : placeholder}
                </option>
                {options.map((opt, idx) => (
                    <option key={idx} value={opt.value}>
                        👤 {opt.label}
                    </option>
                ))}
            </select>
            
            {dragOverType === 'avatar' && (
                <CheckCircleIcon style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    right: 8, 
                    transform: 'translateY(-50%)',
                    color: 'green', 
                    fontSize: 22, 
                    zIndex: 2,
                    pointerEvents: 'none'
                }} />
            )}
            {dragOverType === 'other' && (
                <CancelIcon style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    right: 8, 
                    transform: 'translateY(-50%)',
                    color: 'red', 
                    fontSize: 22, 
                    zIndex: 2,
                    pointerEvents: 'none'
                }} />
            )}
        </div>
    );
};
